import { getImageUrl } from '@/utils/images';
import { getTailwindColor } from '@/utils/colors';
import clsx from 'clsx';

type QuoteCardImage = {
    __isimage: boolean;
    src: string;
    alt_text: string;
    originalSize: {
        width: number;
        height: number;
    };
    focus: {
        x: number;
        y: number;
    };
};

export type QuoteCardProps = {
    quote: string;
    author: string;
    role: string;
    image?: QuoteCardImage | null;
    textColor?: string;
    backgroundColor?: string;
    useLargeCorner?: boolean;
    largeCornerPositions?: Array<'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'>;
};

export const QuoteCard = ({
    quote,
    author,
    role,
    image,
    textColor = 'black',
    backgroundColor = 'beige',
    useLargeCorner = false,
    largeCornerPositions = [],
}: QuoteCardProps) => {
    if (!quote) {
        return null;
    }

    const imageStyle =
        typeof image?.focus?.x === 'number' && typeof image?.focus?.y === 'number'
            ? { objectPosition: `${image.focus.x * 100}% ${image.focus.y * 100}%` }
            : undefined;

    return (
        <div
            data-component="quote-card"
            className={clsx(
                'r-flex r-h-full r-flex-col r-justify-between r-p-8',
                getTailwindColor(textColor, 'text'),
                getTailwindColor(backgroundColor, 'bg'),
                {
                    'r-rounded-2xl': !useLargeCorner,
                    'r-rounded-tl-[90px]': useLargeCorner && largeCornerPositions?.includes('top-left'),
                    'r-rounded-tr-[90px]': useLargeCorner && largeCornerPositions?.includes('top-right'),
                    'r-rounded-bl-[90px]': useLargeCorner && largeCornerPositions?.includes('bottom-left'),
                    'r-rounded-br-[90px]': useLargeCorner && largeCornerPositions?.includes('bottom-right'),
                }
            )}
        >
            <p className="r-m-0 r-mb-5">&quot;{quote}&quot;</p>
            <div className="r-flex r-items-start r-justify-between r-gap-5">
                {image?.src && (
                    <img
                        src={getImageUrl(image.src)}
                        alt={image.alt_text || ''}
                        className="r-max-h-12 r-max-w-24 r-object-contain"
                        style={imageStyle}
                    />
                )}
                <div className="r-text-right">
                    {author && <p className="r-m-0 r-font-semibold" dangerouslySetInnerHTML={{ __html: author }} />}
                    {role && <p className="r-m-0 r-leading-none" dangerouslySetInnerHTML={{ __html: role }} />}
                </div>
            </div>
        </div>
    );
};
